import {
  AckNackRequestTypeEnum,
  IActionListItem,
  IActionListRecordsResult,
  IActionListsResult,
  IActionListStatusResult,
  IAuditLogListResult,
  IBillingDetails,
  IBillingDetailsListResult,
  IContractIssuerListItem,
  IContractIssuerListsResult,
  IEfcContextDataInboundListResult,
  IEfcContextDataOutboundListResult,
  IExceptionListItem,
  IExceptionListsResult,
  ITicket,
  IITicketListResult,
  ILocalAgreementsListResult,
  IPassage,
  IPassagesListResult,
  IPaymentClaim,
  IPaymentClaimsListResult,
  IReceipt,
  IReceiptsListResult,
  ITollCharger,
  ITollingAdminAccessObject,
  ITollServiceProvider,
  ITollServiceProviderListResult,
  LocalAgreement,
  ITransaction,
  ITransactionsListResult,
  IExceptionListChunksResult,
  IPassageEditHistoryResult,
} from 'types/apiTypes';
import { osbApiAxios } from './axiosUtils';
import { AxiosError } from 'axios';
import { IPreset } from 'store/presetStore';
import { DateRange } from 'react-day-picker';
import { format } from 'date-fns';
import {
  convertCopenhagenDateToUtcISOString,
  convertCopenhagenEndOfDayToUtcISOString,
  convertCopenhagenStartOfDayToUtcISOString,
} from './dateTime';

// #region Preset Values
interface IPresetValuesResponse {
  tollChargers: IPreset[];
  tollServiceProviders: IPreset[];
  actionCodes: IPreset[];
  ackTypes: IPreset[];
  actionListRecordTypes: IPreset[];
  actionListTypes: IPreset[];
  actionListStatus: IPreset[];
  paymentClaimTypeOfContent: IPreset[];
  passageType: IPreset[];
  passageStatus: IPreset[];
  localAgreementTypes: IPreset[];
}

export const getPresetValues = async (): Promise<IPresetValuesResponse> => {
  const response = await osbApiAxios.get<IPresetValuesResponse>('/v1/presets');
  return response.data;
};

// #endregion Preset Values

// #region Tolling Admin Access

export const getTollingAdminAccess = async (): Promise<ITollingAdminAccessObject> => {
  const response = await osbApiAxios.get<ITollingAdminAccessObject>('/v1/tollingAdminAccess');
  return response.data;
};

export const postTollingAdminAccess = async (newAccessObject: ITollingAdminAccessObject) => {
  return osbApiAxios.post('/v1/tollingAdminAccess', newAccessObject);
};

// #endregion Tolling Admin Access

// #region AckNackDownload

export const getAckNackDownload = async (
  documentId: string,
  requestType: AckNackRequestTypeEnum
): Promise<string> => {
  const response = await osbApiAxios.get<string>('/v1/Acknowledgement/download', {
    params: {
      documentId,
      requestType,
    },
    transformResponse: (r) => r,
  });
  return response.data;
};

// #endregion AckNackDownload

// #region Billing Details
interface IGetBillingDetailsListQueryParams {
  page: number;
  pageSize?: number;
  sortOrder?: string;
}

export interface IBillingDetailsListFilter {
  apduIdentifier: string;
  aduIdentifier: string;
  createdAt: DateRange | undefined;
  apduDate: DateRange | undefined;
  apduOriginator: string;
  senderId: string;
  recipientId: string;
  startDateTime: DateRange | undefined;
  endDateTime: DateRange | undefined;
  dateOfService: DateRange | undefined;
  feeAmountStart: string;
  feeAmountEnd: string;
  paymentClaimReference: string;
  ackStatus: string;
  obeId: string;
  lpn: string;
  pan: string;
}

/**
 * Interface representing the query parameters for billing details.
 *
 * @property {string} apduIdentifier - The APDU identifier.
 * @property {string} aduIdentifier - The ADU identifier.
 * @property {string} createdAtEnd - The end date for creation timestamp.
 * @property {string} createdAtStart - The start date for creation timestamp.
 * @property {string} apduDateStart - The start date for APDU date.
 * @property {string} apduDateEnd - The end date for APDU date.
 * @property {string} apduOriginator - The APDU originator.
 * @property {string} senderId - The sender ID.
 * @property {string} recipientId - The recipient ID.
 * @property {string} startDateTimeStart - The start date for the start date time.
 * @property {string} startDateTimeEnd - The end date for the start date time.
 * @property {string} endDateTimeStart - The start date for the end date time.
 * @property {string} endDateTimeEnd - The end date for the end date time.
 * @property {string} dateOfServiceStart - The start date for the date of service.
 * @property {string} dateOfServiceEnd - The end date for the date of service.
 * @property {string} feeAmountStart - The start value for the fee amount.
 * @property {string} feeAmountEnd - The end value for the fee amount.
 * @property {string} paymentClaimReference - The payment claim reference.
 * @property {string} ackStatus - The acknowledgment status.
 * @property {string} obeIdEquipmentObuId - The OBE ID (equipment OBU ID).
 * @property {string} licensePlateNumber - The license plate number.
 * @property {string} userIdPan - The user ID (PAN).
 *
 * @remarks
 * This interface is used to define the query parameters for fetching billing details.
 * It includes various filters such as date ranges, identifiers, and statuses.
 * Each property corresponds to a specific filter that can be applied when querying the billing details.
 */
interface IBillingDetailsQueryParams {
  apduIdentifier?: string;
  aduIdentifier?: string;
  createdAtStart: string;
  createdAtEnd: string;
  apduDateStart?: string;
  apduDateEnd?: string;
  apduOriginator?: string;
  senderId?: string;
  recipientId?: string;
  startDateTimeStart?: string;
  startDateTimeEnd?: string;
  endDateTimeStart?: string;
  endDateTimeEnd?: string;
  dateOfServiceStart?: string;
  dateOfServiceEnd?: string;
  feeAmountStart?: string;
  feeAmountEnd?: string;
  paymentClaimReference?: string;
  ackStatus?: string;
  obeIdEquipmentObuId?: string;
  licensePlateNumber?: string;
  userIdPan?: string;
}

/**
 * Fetches a list of billing details based on the provided pagination, filters, and sort order.
 *
 * @param page - The current page number for pagination.
 * @param pageSize - The number of items per page for pagination.
 * @param filters - An object containing the filters to apply to the billing details list.
 * @param filters.createdAt - The creation date filter with optional `from` and `to` properties.
 * @param filters.apduDate - The APDU date filter with optional `from` and `to` properties.
 * @param filters.beginOfPeriod - The begin of period filter with optional `from` and `to` properties.
 * @param filters.endOfPeriod - The end of period filter with optional `from` and `to` properties.
 * @param filters.dateOfService - The date of service filter with optional `from` and `to` properties.
 * @param filters.apduIdentifier - The APDU identifier filter.
 * @param filters.apduOriginator - The APDU originator filter.
 * @param filters.senderId - The information sender ID filter.
 * @param filters.recipientId - The information recipient ID filter.
 * @param filters.aduIdentifier - The ADU identifier filter.
 * @param filters.obeId - The OBE ID (equipment OBU ID) filter.
 * @param filters.lpn - The license plate number filter.
 * @param filters.pan - The user ID (PAN) filter.
 * @param filters.billingDetailsNum - The billing details number filter.
 * @param filters.actionCode - The ADU action code filter.
 * @param filters.acceptedByBo - The accepted by BO filter.
 * @param filters.paymentCaptureId - The payment capture ID filter.
 * @param filters.ackNack - The acknowledgment type filter.
 * @param sortOrder - The order in which to sort the results.
 * @returns A promise that resolves to the billing details list result.
 * @throws AxiosError if the response content type is not "application/json".
 */
export const getBillingDetailsList = async (
  page: number,
  pageSize: number,
  filters: Partial<IBillingDetailsListFilter>,
  sortOrder?: string
): Promise<IBillingDetailsListResult> => {
  const createdAtEnd = filters.createdAt?.to ? filters.createdAt?.to : filters.createdAt?.from;
  const apduDateEnd = filters.apduDate?.to ? filters.apduDate?.to : filters.apduDate?.from;
  const periodBeginOfPeriodEnd = filters.startDateTime?.to
    ? filters.startDateTime?.to
    : filters.startDateTime?.from;
  const periodEndOfPeriodEnd = filters.endDateTime?.to
    ? filters.endDateTime?.to
    : filters.endDateTime?.from;
  const dateOfServiceEnd = filters.dateOfService?.to
    ? filters.dateOfService?.to
    : filters.dateOfService?.from;

  const appliedFilters: IBillingDetailsQueryParams = {
    apduIdentifier: filters.apduIdentifier,
    aduIdentifier: filters.aduIdentifier,

    createdAtStart: filters.createdAt?.from
      ? convertCopenhagenDateToUtcISOString(filters.createdAt.from)
      : '',
    createdAtEnd: createdAtEnd ? convertCopenhagenDateToUtcISOString(createdAtEnd) : '',

    apduDateStart: filters.apduDate?.from
      ? convertCopenhagenDateToUtcISOString(filters.apduDate.from)
      : undefined,
    apduDateEnd: apduDateEnd ? convertCopenhagenDateToUtcISOString(apduDateEnd) : undefined,

    apduOriginator: filters.apduOriginator,
    senderId: filters.senderId,
    recipientId: filters.recipientId,

    startDateTimeStart: filters.startDateTime?.from
      ? convertCopenhagenDateToUtcISOString(filters.startDateTime.from)
      : undefined,
    startDateTimeEnd: periodBeginOfPeriodEnd
      ? convertCopenhagenDateToUtcISOString(periodBeginOfPeriodEnd)
      : undefined,
    endDateTimeStart: filters.endDateTime?.from
      ? convertCopenhagenDateToUtcISOString(filters.endDateTime.from)
      : undefined,
    endDateTimeEnd: periodEndOfPeriodEnd
      ? convertCopenhagenDateToUtcISOString(periodEndOfPeriodEnd)
      : undefined,
    dateOfServiceStart: filters.dateOfService?.from
      ? convertCopenhagenDateToUtcISOString(filters.dateOfService.from)
      : undefined,
    dateOfServiceEnd: dateOfServiceEnd
      ? convertCopenhagenDateToUtcISOString(dateOfServiceEnd)
      : undefined,
    feeAmountStart: filters.feeAmountStart,
    feeAmountEnd: filters.feeAmountEnd,
    paymentClaimReference: filters.paymentClaimReference,
    ackStatus: filters.ackStatus,

    obeIdEquipmentObuId: filters.obeId,
    licensePlateNumber: filters.lpn,
    userIdPan: filters.pan,
  };

  const params: IGetBillingDetailsListQueryParams = {
    page,
    pageSize,
    sortOrder,
    ...appliedFilters,
  };
  const response = await osbApiAxios.get<IBillingDetailsListResult>('/v1/billingDetails', {
    params,
  });

  // @ts-expect-error ignore getContentType type
  const contentType: string = response?.headers?.getContentType() ?? '';

  if (!contentType.includes('application/json')) {
    throw new AxiosError(
      'Response content type is not "application/json"',
      'contentType',
      response.config,
      response.request,
      response
    );
  }
  return response.data;
};

/**
 * Fetches billing details by the given billing details ID.
 *
 * @param {string} billingDetailsId - The ID of the billing details to retrieve.
 * @returns {Promise<IBillingDetails>} A promise that resolves to the billing details.
 */
export const getBillingDetails = async (billingDetailsId: string): Promise<IBillingDetails> => {
  const response = await osbApiAxios.get<IBillingDetails>(`/v1/billingDetails/${billingDetailsId}`);
  return response.data;
};

/**
 * Fetches the billing details file for a given billing details ID.
 *
 * @param billingDetailsId - The ID of the billing details to download.
 * @returns A promise that resolves to the billing details file as a string.
 */
export const getBillingDetailsFile = async (billingDetailsId: string): Promise<string> => {
  const response = await osbApiAxios.get<string>(
    `/v1/billingDetails/${billingDetailsId}/download`,
    {
      transformResponse: (r) => r,
    }
  );
  return response.data;
};
// #endregion Billing Details

// #region Payment Claims
interface IGetPaymentClaimsListQueryParams {
  page: number;
  pageSize?: number;
  sortOrder?: string;
}

export interface IPaymentClaimsListFilter {
  createdAt: DateRange | undefined;
  // apduOriginator: string; // Will always be Øresundsbron
  // senderId: string; // Will always be Øresundsbron
  apduIdentifier: string;
  recipientId: string;
  startDateTime: DateRange | undefined;
  feeAmountStart: string;
  feeAmountEnd: string;
  paymentClaimReference: string;
  typeOfContent: string;
  actionCode: string;
  ackStatus: string;
}

interface IPaymentClaimsListQueryParams {
  createdAtStart: string;
  createdAtEnd: string;
  apduIdentifier?: string;
  recipientId?: string;
  passageDateTimeStart?: string;
  passageDateTimeEnd?: string;
  paymentClaimFeeAmountStart?: string;
  paymentClaimFeeAmountEnd?: string;
  status?: string;
  paymentClaimReference?: string;
  typeOfContent?: string;
  actionCode?: string;
  ackStatus?: string;
}

export const getPaymentClaimsList = async (
  page: number,
  pageSize: number,
  filters: Partial<IPaymentClaimsListFilter>,
  sortOrder?: string
): Promise<IPaymentClaimsListResult> => {
  const createdAtEnd = filters.createdAt?.to ? filters.createdAt?.to : filters.createdAt?.from;

  const startDateTimeEnd = filters.startDateTime?.to
    ? filters.startDateTime?.to
    : filters.startDateTime?.from;

  const appliedFilters: IPaymentClaimsListQueryParams = {
    createdAtStart: filters.createdAt?.from
      ? convertCopenhagenDateToUtcISOString(filters.createdAt.from)
      : '',
    createdAtEnd: createdAtEnd ? convertCopenhagenDateToUtcISOString(createdAtEnd) : '',

    apduIdentifier: filters.apduIdentifier,
    recipientId: filters.recipientId,

    passageDateTimeStart: filters.startDateTime?.from
      ? convertCopenhagenDateToUtcISOString(filters.startDateTime.from)
      : undefined,
    passageDateTimeEnd: startDateTimeEnd
      ? convertCopenhagenDateToUtcISOString(startDateTimeEnd)
      : undefined,

    paymentClaimFeeAmountStart: filters.feeAmountStart,
    paymentClaimFeeAmountEnd: filters.feeAmountEnd,
    paymentClaimReference: filters.paymentClaimReference,
    typeOfContent: filters.typeOfContent,
    ackStatus: filters.ackStatus,
  };

  const params: IGetPaymentClaimsListQueryParams = {
    page,
    pageSize,
    sortOrder,
    ...appliedFilters,
  };
  const response = await osbApiAxios.get<IPaymentClaimsListResult>('/v1/paymentClaims', {
    params,
  });

  // @ts-expect-error ignore getContentType type
  const contentType: string = response?.headers?.getContentType() ?? '';

  if (!contentType.includes('application/json')) {
    throw new AxiosError(
      'Response content type is not "application/json"',
      'contentType',
      response.config,
      response.request,
      response
    );
  }
  return response.data;
};

export const getPaymentClaimDetails = async (paymentClaimsId: string): Promise<IPaymentClaim> => {
  const response = await osbApiAxios.get<IPaymentClaim>(`/v1/paymentClaims/${paymentClaimsId}`);
  return response.data;
};

export const getPaymentClaimDetailsFile = async (paymentClaimsId: string): Promise<string> => {
  const response = await osbApiAxios.get<string>(`/v1/paymentClaims/${paymentClaimsId}/download`, {
    transformResponse: (r) => r,
  });
  return response.data;
};

export const postPaymentClaimResend = async (paymentClaimId: string) => {
  return osbApiAxios.post<string>(`/v1/paymentClaims/${paymentClaimId}/resend`);
};

// #endregion Payment Claims

// #region Passages
interface IGetPassagesListQueryParams {
  page: number;
  pageSize?: number;
  sortOrder?: string;
}

export interface IPassagesListFilter {
  passageId: string | undefined;
  lanePassageId: string | undefined;
  timestampStart: Date | undefined;
  timestampEnd: Date | undefined;
  vehicleClass: string | undefined;
  lpnTotal: string;
  lpnFront: string;
  lpnRear: string;
  pan: string;
  receiptId: string;
  relatedBillingDetails: string;

  passageType: string;
  status: string;
  issuer: string;
  localAgreement: string;
  laneNumber: string;
}

interface IPassagesListQueryParams {
  passageId?: string;
  lanePassageId?: string;
  timestampStart?: string;
  timestampEnd?: string;
  vehicleClass?: string;
  lpnTotal?: string;
  lpnFront?: string;
  lpnRear?: string;
  pan?: string;
  receiptId?: string;
  relatedBillingDetails?: boolean;

  passageType?: string;
  status?: string;
  issuer?: string;
  agreementType?: string;
  laneNumber?: string;
}

export const getPassagesList = async (
  page: number,
  pageSize: number,
  filters: Partial<IPassagesListFilter>,
  sortOrder?: string
): Promise<IPassagesListResult> => {
  const appliedFilters: IPassagesListQueryParams = {
    passageId: filters.passageId,
    lanePassageId: filters.lanePassageId,

    timestampStart: filters.timestampStart
      ? convertCopenhagenDateToUtcISOString(filters.timestampStart)
      : undefined,
    timestampEnd: filters.timestampEnd
      ? convertCopenhagenDateToUtcISOString(filters.timestampEnd)
      : undefined,

    vehicleClass: filters.vehicleClass,
    lpnTotal: filters.lpnTotal,
    pan: filters.pan,
    receiptId: filters.receiptId,

    relatedBillingDetails: filters.relatedBillingDetails
      ? filters.relatedBillingDetails === 'true'
      : undefined,

    passageType: filters.passageType,
    status: filters.status,
    issuer: filters.issuer,
    agreementType: filters.localAgreement,
    laneNumber: filters.laneNumber,
  };

  const params: IGetPassagesListQueryParams = {
    page,
    pageSize,
    sortOrder,
    ...appliedFilters,
  };
  const response = await osbApiAxios.get<IPassagesListResult>('/v1/passageReports', {
    params,
  });

  // @ts-expect-error ignore getContentType type
  const contentType: string = response?.headers?.getContentType() ?? '';

  if (!contentType.includes('application/json')) {
    throw new AxiosError(
      'Response content type is not "application/json"',
      'contentType',
      response.config,
      response.request,
      response
    );
  }
  return response.data;
};

export const getPassageDetails = async (lanePassageId: string): Promise<IPassage> => {
  const response = await osbApiAxios.get<IPassage>(`/v1/passageReports/${lanePassageId}`);
  return response.data;
};

export const getPassageEditHistory = async (
  lanePassageId: string
): Promise<IPassageEditHistoryResult> => {
  const response = await osbApiAxios.get<IPassageEditHistoryResult>(
    `/v1/passageReports/${lanePassageId}/editHistory`
  );
  return response.data;
};

export const getPassageDetailsFile = async (lanePassageId: string): Promise<string> => {
  const response = await osbApiAxios.get<string>(`/v1/passageReports/${lanePassageId}/download`, {
    transformResponse: (r) => r,
  });
  return response.data;
};

// #endregion Passages

// #region Receipts
interface IGetReceiptsListQueryParams {
  page: number;
  pageSize?: number;
  sortOrder?: string;
}

export interface IReceiptsListFilter {
  receiptId: string | undefined;
  passageId: string | undefined;
  transactionEnd: DateRange | undefined;
  userId: string | undefined;
  state: string;
  amountFrom: string;
  amountTo: string;
  currency: string;
  paymentMethod: string;
  licensePlateNumber: string;
  laneId: string;
  laneType: string;
  status: string;
}

interface IReceiptsListQueryParams {
  receiptId?: string;
  passageId?: string;
  transactionEndFrom?: string;
  transactionEndTo?: string;
  userId?: number;
  state?: number;
  amountFrom?: number;
  amountTo?: number;
  currency?: string;
  paymentMethod?: number;
  licensePlateNumber?: string;
  laneId?: number;
  laneType?: number;
  status?: number;
}

export const getReceiptsList = async (
  page: number,
  pageSize: number,
  filters: Partial<IReceiptsListFilter>,
  sortOrder?: string
): Promise<IReceiptsListResult> => {
  const transactionEnd = filters.transactionEnd?.to
    ? filters.transactionEnd?.to
    : filters.transactionEnd?.from;

  const appliedFilters: IReceiptsListQueryParams = {
    receiptId: filters.receiptId,
    passageId: filters.passageId,

    transactionEndFrom: filters.transactionEnd?.from
      ? convertCopenhagenDateToUtcISOString(filters.transactionEnd.from)
      : undefined,
    transactionEndTo: transactionEnd
      ? convertCopenhagenDateToUtcISOString(transactionEnd)
      : undefined,

    userId: filters.userId ? parseInt(filters.userId) : undefined,
    state: filters.state ? parseInt(filters.state) : undefined,
    amountFrom: filters.amountFrom ? parseFloat(filters.amountFrom) : undefined,
    amountTo: filters.amountTo ? parseFloat(filters.amountTo) : undefined,
    currency: filters.currency,
    licensePlateNumber: filters.licensePlateNumber,
    paymentMethod: filters.paymentMethod ? parseInt(filters.paymentMethod) : undefined,
    laneId: filters.laneId ? parseInt(filters.laneId) : undefined,
    laneType: filters.laneType ? parseInt(filters.laneType) : undefined,
    status: filters.status ? parseInt(filters.status, 10) : undefined,
  };

  const params: IGetReceiptsListQueryParams = {
    page,
    pageSize,
    sortOrder,
    ...appliedFilters,
  };
  const response = await osbApiAxios.get<IReceiptsListResult>('/v1/receipt', {
    params,
  });

  // @ts-expect-error ignore getContentType type
  const contentType: string = response?.headers?.getContentType() ?? '';

  if (!contentType.includes('application/json')) {
    throw new AxiosError(
      'Response content type is not "application/json"',
      'contentType',
      response.config,
      response.request,
      response
    );
  }
  return response.data;
};

export const getReceiptDetails = async (receiptId: string): Promise<IReceipt> => {
  const response = await osbApiAxios.get<IReceipt>(`/v1/receipt/${receiptId}`);
  return response.data;
};

export const getReceiptDetailsFile = async (receiptId: string): Promise<string> => {
  const response = await osbApiAxios.get<string>(`/v1/receipt/${receiptId}/download`, {
    transformResponse: (r) => r,
  });
  return response.data;
};

// #endregion Receipts

// #region Audit Log
interface IGetAuditLogListQueryParams {
  page: number;
  pageSize?: number;
  sortOrder?: string;
}
export interface IAuditLogListFilter {
  actor: string;
  obeEquipmentId: string;
  eventSeverity: string;
  date: DateRange | undefined;
  freeText: string;
}
interface IAuditLogListQueryParams {
  actor?: string;
  obeEquipmentId?: string;
  eventSeverity?: string;
  startTime?: string;
  endTime?: string;
  freeText?: string;
}
export const getAuditLogList = async (
  page: number,
  pageSize: number,
  filters: Partial<IAuditLogListFilter>,
  sortOrder?: string
): Promise<IAuditLogListResult> => {
  const endTime = filters.date?.to ? filters.date?.to : filters.date?.from;

  const appliedFilters: IAuditLogListQueryParams = {
    startTime: filters.date?.from ? format(filters.date?.from, 'yyyy-MM-dd') : undefined,
    endTime: endTime ? format(endTime, 'yyyy-MM-dd') : undefined,
    actor: filters.actor,
    obeEquipmentId: filters.obeEquipmentId,
    eventSeverity: filters.eventSeverity,
    freeText: filters.freeText,
  };
  const params: IGetAuditLogListQueryParams = {
    page,
    pageSize,
    sortOrder,
    ...appliedFilters,
  };
  const response = await osbApiAxios.get<IAuditLogListResult>('/v1/auditLogEvents', {
    params,
  });
  return response.data;
};
// #endregion Audit Log

// #region Toll Charger Data

export const getTollChargerDetails = async (): Promise<ITollCharger> => {
  const response = await osbApiAxios.get<ITollCharger>(`/v1/TollChargerData/`);
  return response.data;
};

export const putTollChargerDetails = async (data: ITollCharger) => {
  return osbApiAxios.put<ITollCharger>(
    `/v1/TollChargerData`,
    data
    //   {
    //   tollServiceProvider: data,
    //   hash,
    // }
  );
};

export const postTollChargerMessage = async (actorCodes: string[]) => {
  return osbApiAxios.post<string>(`/v1/TollChargerData/send`, { actorCodes });
};

// #endregion Toll Charger Data

// #region Toll Service Provider
interface IGetTollServiceProviderListQueryParams {
  page: number;
  pageSize?: number;
  sortOrder?: string;
}
export interface ITollServiceProviderListFilter {
  entityType: string;
  actorId: string;
  name: string;
  providerIdentifier: string;
  countryCode: string;
}
interface ITollServiceProviderListQueryParams {
  entityType?: string;
  actorId?: string;
  name?: string;
  providerIdentifier?: string;
  countryCode?: string;
}
export const getTollServiceProviderList = async (
  page: number,
  pageSize: number,
  filters: Partial<ITollServiceProviderListFilter>,
  sortOrder?: string
): Promise<ITollServiceProviderListResult> => {
  const appliedFilters: ITollServiceProviderListQueryParams = {
    entityType: filters.entityType,
    actorId: filters.actorId,
    name: filters.name,
    providerIdentifier: filters.providerIdentifier,
    countryCode: filters.countryCode,
  };
  const params: IGetTollServiceProviderListQueryParams = {
    page,
    pageSize,
    sortOrder,
    ...appliedFilters,
  };
  const response = await osbApiAxios.get<ITollServiceProviderListResult>(
    '/v1/tollServiceProviderData',
    {
      params,
    }
  );
  return response.data;
};

export const getTollServiceProviderDetails = async (id: string): Promise<ITollServiceProvider> => {
  const response = await osbApiAxios.get<ITollServiceProvider>(`/v1/tollServiceProviderData/${id}`);
  return response.data;
};

export const postTollServiceProvider = async (newTollServiceProvider: ITollServiceProvider) => {
  return osbApiAxios.post<ITollServiceProvider>(
    '/v1/tollServiceProviderData',
    newTollServiceProvider
  );
};

export const putTollServiceProvider = async (newTollServiceProvider: ITollServiceProvider) => {
  return osbApiAxios.put<ITollServiceProvider>(
    `/v1/tollServiceProviderData/${newTollServiceProvider.id}`,
    newTollServiceProvider
  );
};

// #endregion Toll Service Provider

// #region Efc Context Data Outbound
interface IGetEfcContextDataOutboundListQueryParams {
  page: number;
  pageSize?: number;
  sortOrder?: string;
}
export interface IEfcContextDataOutboundListFilter {
  recipientId: string;
  senderId: string;
  apduIdentifier: string;
  apduOriginator: string;

  apduDate: DateRange | undefined;
  sent: DateRange | undefined;
}
interface IEfcContextDataOutboundListQueryParams {
  recipientId?: string;
  senderId?: string;
  apduIdentifier?: string;
  apduOriginator?: string;

  apduDateStart?: string;
  apduDateEnd?: string;
  sentStart?: string;
  sentEnd?: string;
}
export const getEfcContextDataOutboundList = async (
  page: number,
  pageSize: number,
  filters: Partial<IEfcContextDataOutboundListFilter>,
  sortOrder?: string
): Promise<IEfcContextDataOutboundListResult> => {
  const apduDateEnd = filters.apduDate?.to ? filters.apduDate?.to : filters.apduDate?.from;
  const sentEnd = filters.sent?.to ? filters.sent?.to : filters.sent?.from;

  const appliedFilters: IEfcContextDataOutboundListQueryParams = {
    recipientId: filters.recipientId,
    senderId: filters.senderId,
    apduIdentifier: filters.apduIdentifier,
    apduOriginator: filters.apduOriginator,

    apduDateStart: filters.apduDate?.from
      ? convertCopenhagenStartOfDayToUtcISOString(filters.apduDate.from)
      : undefined,
    apduDateEnd: apduDateEnd ? convertCopenhagenEndOfDayToUtcISOString(apduDateEnd) : undefined,
    sentStart: filters.sent?.from
      ? convertCopenhagenStartOfDayToUtcISOString(filters.sent.from)
      : undefined,
    sentEnd: sentEnd ? convertCopenhagenEndOfDayToUtcISOString(sentEnd) : undefined,
  };
  const params: IGetEfcContextDataOutboundListQueryParams = {
    page,
    pageSize,
    sortOrder,
    ...appliedFilters,
  };
  const response = await osbApiAxios.get<IEfcContextDataOutboundListResult>(
    '/v1/OutboundEfcContextData',
    {
      params,
    }
  );
  return response.data;
};

export const getEfcContextDataOutboundFile = async (
  outboundEfcContextId: string
): Promise<string> => {
  const response = await osbApiAxios.get<string>(
    `/v1/OutboundEfcContextData/${outboundEfcContextId}/download`,
    {
      transformResponse: (r) => r,
    }
  );
  return response.data;
};

// #endregion Efc Context Data Outbound

// #region Efc Context Data Inbound
interface IGetEfcContextDataInboundListQueryParams {
  page: number;
  pageSize?: number;
  sortOrder?: string;
}
export interface IEfcContextDataInboundListFilter {
  recipientId: string;
  senderId: string;
  apduIdentifier: string;
  apduOriginator: string;

  apduDate: DateRange | undefined;
  received: DateRange | undefined;
}
interface IEfcContextDataInboundListQueryParams {
  recipientId?: string;
  senderId?: string;
  apduIdentifier?: string;
  apduOriginator?: string;

  apduDateStart?: string;
  apduDateEnd?: string;
  receivedStart?: string;
  receivedEnd?: string;
}
export const getEfcContextDataInboundList = async (
  page: number,
  pageSize: number,
  filters: Partial<IEfcContextDataInboundListFilter>,
  sortOrder?: string
): Promise<IEfcContextDataInboundListResult> => {
  const apduDateEnd = filters.apduDate?.to ? filters.apduDate?.to : filters.apduDate?.from;
  const receivedEnd = filters.received?.to ? filters.received?.to : filters.received?.from;

  const appliedFilters: IEfcContextDataInboundListQueryParams = {
    recipientId: filters.recipientId,
    senderId: filters.senderId,
    apduIdentifier: filters.apduIdentifier,
    apduOriginator: filters.apduOriginator,

    apduDateStart: filters.apduDate?.from
      ? convertCopenhagenStartOfDayToUtcISOString(filters.apduDate.from)
      : undefined,
    apduDateEnd: apduDateEnd ? convertCopenhagenEndOfDayToUtcISOString(apduDateEnd) : undefined,
    receivedStart: filters.received?.from
      ? convertCopenhagenStartOfDayToUtcISOString(filters.received.from)
      : undefined,
    receivedEnd: receivedEnd ? convertCopenhagenEndOfDayToUtcISOString(receivedEnd) : undefined,
  };
  const params: IGetEfcContextDataInboundListQueryParams = {
    page,
    pageSize,
    sortOrder,
    ...appliedFilters,
  };
  const response = await osbApiAxios.get<IEfcContextDataInboundListResult>(
    '/v1/InboundEfcContextData',
    {
      params,
    }
  );
  return response.data;
};

export const getEfcContextDataInboundFile = async (
  inboundEfcContextId: string
): Promise<string> => {
  const response = await osbApiAxios.get<string>(
    `/v1/InboundEfcContextData/${inboundEfcContextId}/download`,
    {
      transformResponse: (r) => r,
    }
  );
  return response.data;
};

// #endregion Efc Context Data Inbound

// #region Exception Lists
interface IGetExceptionListsQueryParams {
  page: number;
  pageSize?: number;
  sortOrder?: string;
}
export interface IExceptionListsFilter {
  apduDate: DateRange | undefined;
  exceptionListType: string;
  obeEquipmentId: string;
  lpn: string;
  apduOriginator: string;
  senderId: string;
  recipientId: string;
  apduIdentifier: string;
  aduIdentifier: string;
  exceptionListVersion: string;
  exceptionValidityStart: DateRange | undefined;

  actionCode: string;
  ackStatus: string;
}

interface IExceptionListsQueryParams {
  apduDateFrom?: string;
  apduDateTo?: string;
  exceptionListType?: string;
  obeEquipmentId?: string;
  lpn?: string;
  apduOriginator?: string;
  senderId?: string;
  recipientId?: string;
  apduIdentifier?: string;
  aduIdentifier?: string;
  exceptionListVersion?: string;
  exceptionValidityStartFrom?: string;
  exceptionValidityStartTo?: string;

  actionCode?: string;
  ackStatus?: string;
}

export const getExceptionLists = async (
  page: number,
  pageSize: number,
  filters: Partial<IExceptionListsFilter>,
  sortOrder?: string
): Promise<IExceptionListsResult> => {
  const apduDateTo = filters.apduDate?.to ? filters.apduDate?.to : filters.apduDate?.from;
  const exceptionValidityStartTo = filters.exceptionValidityStart?.to
    ? filters.exceptionValidityStart?.to
    : filters.exceptionValidityStart?.from;

  const appliedFilters: IExceptionListsQueryParams = {
    apduDateFrom: filters.apduDate?.from ? format(filters.apduDate?.from, 'yyyy-MM-dd') : undefined,
    apduDateTo: apduDateTo ? format(apduDateTo, 'yyyy-MM-dd') : undefined,
    exceptionListType: filters.exceptionListType,
    obeEquipmentId: filters.obeEquipmentId,
    lpn: filters.lpn,
    apduOriginator: filters.apduOriginator,
    senderId: filters.senderId,
    recipientId: filters.recipientId,
    apduIdentifier: filters.apduIdentifier,
    aduIdentifier: filters.aduIdentifier,
    exceptionListVersion: filters.exceptionListVersion,
    exceptionValidityStartFrom: filters.exceptionValidityStart?.from
      ? format(filters.exceptionValidityStart?.from, 'yyyy-MM-dd')
      : undefined,
    exceptionValidityStartTo: exceptionValidityStartTo
      ? format(exceptionValidityStartTo, 'yyyy-MM-dd')
      : undefined,
    actionCode: filters.actionCode,

    ackStatus: filters.ackStatus,
  };

  const params: IGetExceptionListsQueryParams = {
    page,
    pageSize,
    sortOrder,
    ...appliedFilters,
  };
  const response = await osbApiAxios.get<IExceptionListsResult>('/v1/exceptionList', {
    params,
  });
  return response.data;
};

export const getExceptionListDetails = async (
  exceptionListId: string
): Promise<IExceptionListItem> => {
  const response = await osbApiAxios.get<IExceptionListItem>(
    `/v1/exceptionList/${exceptionListId}`
  );
  return response.data;
};

export const getExceptionListDetailsFile = async (exceptionListId: string): Promise<string> => {
  const response = await osbApiAxios.get<string>(`/v1/exceptionList/${exceptionListId}/download`, {
    transformResponse: (r) => r,
  });
  return response.data;
};

export const getExceptionListChunkFile = async (apduIdentifier: string): Promise<string> => {
  const response = await osbApiAxios.get<string>(
    `/v1/exceptionList/full/${apduIdentifier}/download`,
    {
      transformResponse: (r) => r,
    }
  );
  return response.data;
};

interface IGetExceptionListChunksQueryParams {
  AduIdentifier: string;
  page: number;
  pageSize?: number;
  sortOrder?: string;
}

export const getExceptionListChunks = async (
  exceptionListId: string,
  aduIdentifier: string,
  page: number,
  pageSize: number,
  sortOrder?: string
): Promise<IExceptionListChunksResult> => {
  const params: IGetExceptionListChunksQueryParams = {
    page: page,
    pageSize,
    AduIdentifier: aduIdentifier,
    sortOrder,
  };

  const response = await osbApiAxios.get<IExceptionListChunksResult>(
    `/v1/exceptionList/${exceptionListId}/chunks`,
    { params }
  );
  return response.data;
};

// #endregion Exception Lists

// #region Contract Issuer Lists
interface IGetContractIssuerListsQueryParams {
  page: number;
  pageSize?: number;
  sortOrder?: string;
}
export interface IContractIssuerListsFilter {
  apduDate: DateRange | undefined;
  apduOriginator: string;
  senderId: string;
  recipientId: string;
  apduIdentifier: string;
  aduIdentifier: string;
  version: string;
  ackStatus: string;
}

interface IContractIssuerListsQueryParams {
  apduDateFrom?: string;
  apduDateTo?: string;
  apduOriginator?: string;
  senderId?: string;
  recipientId?: string;
  apduIdentifier?: string;
  aduIdentifier?: string;
  version?: string;
  ackStatus?: string;
}

export const getContractIssuerLists = async (
  page: number,
  pageSize: number,
  filters: Partial<IContractIssuerListsFilter>,
  sortOrder?: string
): Promise<IContractIssuerListsResult> => {
  const apduDateTo = filters.apduDate?.to ? filters.apduDate?.to : filters.apduDate?.from;

  const appliedFilters: IContractIssuerListsQueryParams = {
    apduDateFrom: filters.apduDate?.from
      ? convertCopenhagenStartOfDayToUtcISOString(filters.apduDate.from)
      : undefined,
    apduDateTo: apduDateTo ? convertCopenhagenEndOfDayToUtcISOString(apduDateTo) : undefined,
    apduOriginator: filters.apduOriginator,
    senderId: filters.senderId,
    recipientId: filters.recipientId,
    apduIdentifier: filters.apduIdentifier,
    aduIdentifier: filters.aduIdentifier,
    version: filters.version,
    ackStatus: filters.ackStatus,
  };

  const params: IGetContractIssuerListsQueryParams = {
    page,
    pageSize,
    sortOrder,
    ...appliedFilters,
  };
  const response = await osbApiAxios.get<IContractIssuerListsResult>('/v1/contractIssuerList', {
    params,
  });
  return response.data;
};

export const getContractIssuerListDetails = async (
  contractIssuerListId: string
): Promise<IContractIssuerListItem> => {
  const response = await osbApiAxios.get<IContractIssuerListItem>(
    `/v1/contractIssuerList/${contractIssuerListId}`
  );
  return response.data;
};

export const getContractIssuerListFile = async (contractIssuerListId: string): Promise<string> => {
  const response = await osbApiAxios.get<string>(
    `/v1/contractIssuerList/${contractIssuerListId}/download`,
    {
      transformResponse: (r) => r,
    }
  );
  return response.data;
};

// #endregion Contract Issuer Lists

// #region Action Lists

interface IGetActionListsQueryParams {
  page: number;
  pageSize?: number;
  sortOrder?: string;
}
export interface IActionListsFilter {
  listId: string;
  listCreationUtc: DateRange | undefined;
  actionListType: string;
  actionListVersion: string;
  pan: string;
  serialNumber: string;
  lpn: string;
  stateOfRegistration: string;
  exceptionCode: string;
  allowForPassage: boolean;
  vehicleClass: string;
  actionListStatus: string;
  deleteEntry: boolean;
  actionListRecordType: string;
}

interface IActionListsQueryParams {
  listId?: string;
  listCreationUtcStart?: string;
  listCreationUtcEnd?: string;
  actionListType?: number;
  actionListVersion?: number;
  pan?: string;
  serialNumber?: string;
  lpn?: string;
  stateOfRegistration?: string;
  exceptionCode?: string;
  allowForPassage?: boolean;
  actionListStatus?: string;
  deleteEntry?: boolean;
  actionListRecordType?: number;
}

export const getActionLists = async (
  page: number,
  pageSize: number,
  filters: Partial<IActionListsFilter>,
  sortOrder?: string
): Promise<IActionListsResult> => {
  const listCreationUtcEnd = filters.listCreationUtc?.to
    ? filters.listCreationUtc?.to
    : filters.listCreationUtc?.from;

  const appliedFilters: IActionListsQueryParams = {
    listId: filters.listId,
    listCreationUtcStart: filters.listCreationUtc?.from
      ? convertCopenhagenDateToUtcISOString(filters.listCreationUtc.from)
      : undefined,
    listCreationUtcEnd: listCreationUtcEnd
      ? convertCopenhagenDateToUtcISOString(listCreationUtcEnd)
      : undefined,
    actionListType: filters.actionListType ? parseInt(filters.actionListType) : undefined,
    actionListVersion: filters.actionListVersion ? parseInt(filters.actionListVersion) : undefined,
    pan: filters.pan,
    serialNumber: filters.serialNumber,
    lpn: filters.lpn,
    stateOfRegistration: filters.stateOfRegistration,
    exceptionCode: filters.exceptionCode,
    allowForPassage: filters.allowForPassage,
    actionListStatus: filters.actionListStatus,
    deleteEntry: filters.deleteEntry,
    actionListRecordType: filters.actionListRecordType
      ? parseInt(filters.actionListRecordType)
      : undefined,
  };

  const params: IGetActionListsQueryParams = {
    page,
    pageSize,
    sortOrder,
    ...appliedFilters,
  };
  const response = await osbApiAxios.get<IActionListsResult>('/v1/actionList', {
    params,
  });
  return response.data;
};

export const getActionListRecords = async (
  actionListId: string,
  page: number,
  pageSize: number,
  filters: Partial<IActionListsFilter>,
  sortOrder?: string
): Promise<IActionListRecordsResult> => {
  const listCreationUtcEnd = filters.listCreationUtc?.to
    ? filters.listCreationUtc?.to
    : filters.listCreationUtc?.from;

  const appliedFilters: IActionListsQueryParams = {
    listId: filters.listId,
    listCreationUtcStart: filters.listCreationUtc?.from
      ? convertCopenhagenDateToUtcISOString(filters.listCreationUtc.from)
      : undefined,
    listCreationUtcEnd: listCreationUtcEnd
      ? convertCopenhagenDateToUtcISOString(listCreationUtcEnd)
      : undefined,
    actionListType: filters.actionListType ? parseInt(filters.actionListType) : undefined,
    actionListVersion: filters.actionListVersion ? parseInt(filters.actionListVersion) : undefined,
    pan: filters.pan,
    serialNumber: filters.serialNumber,
    lpn: filters.lpn,
    stateOfRegistration: filters.stateOfRegistration,
    exceptionCode: filters.exceptionCode,
    allowForPassage: filters.allowForPassage,
    actionListStatus: filters.actionListStatus,
    deleteEntry: filters.deleteEntry,
    // @ts-expect-error actionListRecordType is called type in this context
    type: filters.actionListRecordType ? parseInt(filters.actionListRecordType) : undefined,
  };

  const params: IGetActionListsQueryParams = {
    page,
    pageSize,
    sortOrder,
    ...appliedFilters,
  };
  const response = await osbApiAxios.get<IActionListRecordsResult>(
    `/v1/actionList/${actionListId}/record`,
    {
      params,
    }
  );
  return response.data;
};

export const getActionListDetails = async (actionListId: string): Promise<IActionListItem> => {
  const response = await osbApiAxios.get<IActionListItem>(`/v1/actionList/${actionListId}`);
  return response.data;
};

export const getActionListStatuses = async (
  actionListId: string
): Promise<IActionListStatusResult> => {
  const response = await osbApiAxios.get<IActionListStatusResult>(
    `/v1/actionList/${actionListId}/status`
  );
  return response.data;
};

export const postFullActionList = async (): Promise<string> => {
  const response = await osbApiAxios.post<string>(`/v1/actionList/sendFull`);
  return response.data;
};

export const postIncrementalActionList = async (actionListId: string): Promise<string> => {
  const response = await osbApiAxios.post<string>(`/v1/actionListRecord/${actionListId}/resend`);
  return response.data;
};

// #endregion Action Lists

// #region iTicket

interface IGetITicketListQueryParams {
  page: number;
  pageSize?: number;
  sortOrder?: string;
}

export interface IITicketListFilter {
  iTicketNumber: string;
  type: string;
  localAgreement: string;
  licensePlateNumber: string;
  country: number;
  iTicketState: string;
  issuer: string;
  vehicleCategory: string;

  passageId: string;
  isCredited: boolean;
  amountMin: string;
  amountMax: string;
  currencyCode: string;
  deletedAt: DateRange | undefined;
  deletedBy: string;
  deletedReason: string;

  ticketCreated: DateRange | undefined;
  ticketValidFrom: DateRange | undefined;
  ticketValidTo: DateRange | undefined;
  consumedAt: DateRange | undefined;

  customerId: string | undefined;
}

interface IITicketListQueryParams {
  iTicketNumber?: string;
  type?: string;
  localAgreement?: string;
  licensePlateNumber?: string;
  country?: number;
  iTicketState?: string;
  issuer?: string;
  vehicleCategory?: string;

  passageId?: string;
  isCredited?: boolean;
  amountMin?: string;
  amountMax?: string;
  currencyCode?: string;
  deletedAtStart?: string;
  deletedAtEnd?: string;
  deletedBy?: string;
  deletedReason?: string;

  ticketCreatedStart?: string;
  ticketCreatedEnd?: string;
  ticketValidFromStart?: string;
  ticketValidFromEnd?: string;
  ticketValidToStart?: string;
  ticketValidToEnd?: string;
  consumedAtStart?: string;
  consumedAtEnd?: string;

  customerId?: string;
}

export const getITicketList = async (
  page: number,
  pageSize: number,
  filters: Partial<IITicketListFilter>,
  sortOrder?: string
): Promise<IITicketListResult> => {
  const ticketCreatedEnd = filters.ticketCreated?.to
    ? filters.ticketCreated?.to
    : filters.ticketCreated?.from;
  const ticketValidFromEnd = filters.ticketValidFrom?.to
    ? filters.ticketValidFrom?.to
    : filters.ticketValidFrom?.from;
  const ticketValidToEnd = filters.ticketValidTo?.to
    ? filters.ticketValidTo?.to
    : filters.ticketValidTo?.from;
  const consumedAtEnd = filters.consumedAt?.to ? filters.consumedAt?.to : filters.consumedAt?.from;
  const deletedAtEnd = filters.deletedAt?.to ? filters.deletedAt?.to : filters.deletedAt?.from;

  const appliedFilters: IITicketListQueryParams = {
    iTicketNumber: filters.iTicketNumber,
    type: filters.type,
    localAgreement: filters.localAgreement,
    licensePlateNumber: filters.licensePlateNumber,
    country: filters.country,
    iTicketState: filters.iTicketState,
    issuer: filters.issuer,
    vehicleCategory: filters.vehicleCategory,

    passageId: filters.passageId,
    isCredited: filters.isCredited,
    amountMin: filters.amountMin,
    amountMax: filters.amountMax,
    currencyCode: filters.currencyCode,
    deletedAtStart: filters.deletedAt?.from
      ? convertCopenhagenDateToUtcISOString(filters.deletedAt.from)
      : undefined,
    deletedAtEnd: deletedAtEnd ? convertCopenhagenEndOfDayToUtcISOString(deletedAtEnd) : undefined,

    deletedBy: filters.deletedBy,
    deletedReason: filters.deletedReason,

    // ticketCreated has a date picker
    ticketCreatedStart: filters.ticketCreated?.from
      ? convertCopenhagenStartOfDayToUtcISOString(filters.ticketCreated.from)
      : undefined,
    ticketCreatedEnd: ticketCreatedEnd
      ? convertCopenhagenEndOfDayToUtcISOString(ticketCreatedEnd)
      : undefined,
    // ticketValidFrom has a date picker
    ticketValidFromStart: filters.ticketValidFrom?.from
      ? convertCopenhagenStartOfDayToUtcISOString(filters.ticketValidFrom.from)
      : undefined,
    ticketValidFromEnd: ticketValidFromEnd
      ? convertCopenhagenEndOfDayToUtcISOString(ticketValidFromEnd)
      : undefined,
    // ticketValidTo has a date picker
    ticketValidToStart: filters.ticketValidTo?.from
      ? convertCopenhagenStartOfDayToUtcISOString(filters.ticketValidTo.from)
      : undefined,
    ticketValidToEnd: ticketValidToEnd
      ? convertCopenhagenEndOfDayToUtcISOString(ticketValidToEnd)
      : undefined,

    // consumedAt has a date time picker
    consumedAtStart: filters.consumedAt?.from
      ? convertCopenhagenDateToUtcISOString(filters.consumedAt.from)
      : undefined,
    consumedAtEnd: consumedAtEnd ? convertCopenhagenDateToUtcISOString(consumedAtEnd) : undefined,

    customerId: filters.customerId,
  };

  const params: IGetITicketListQueryParams = {
    page,
    pageSize,
    sortOrder,
    ...appliedFilters,
  };
  const response = await osbApiAxios.get<IITicketListResult>('/v1/iTickets', {
    params,
  });

  // @ts-expect-error ignore getContentType type
  const contentType: string = response?.headers?.getContentType() ?? '';

  if (!contentType.includes('application/json')) {
    throw new AxiosError(
      'Response content type is not "application/json"',
      'contentType',
      response.config,
      response.request,
      response
    );
  }
  return response.data;
};

export const getITicketDetails = async (id: string): Promise<ITicket> => {
  const response = await osbApiAxios.get<ITicket>(`/v1/iTickets/${id}`);
  return response.data;
};

// #endregion iTicket

// #region Local Agreements
interface IGetLocalAgreementsListQueryParams {
  page: number;
  pageSize?: number;
  sortOrder?: string;
}
export interface ILocalAgreementsListFilter {
  agreementId: string;
  endDate: DateRange | undefined;
  status: string;
  tollServiceProvider: string;
  agreementType: string;
  lpn: string;
}
interface ILocalAgreementsListQueryParams {
  agreementId?: string;
  endDateFrom?: string;
  endDateTo?: string;
  status?: string;
  tollServiceProvider?: string;
  agreementType?: string;
  lpn?: string;
}
export const getLocalAgreementsList = async (
  page: number,
  pageSize: number,
  filters: Partial<ILocalAgreementsListFilter>,
  sortOrder?: string
): Promise<ILocalAgreementsListResult> => {
  const endDateTo = filters.endDate?.to ? filters.endDate?.to : filters.endDate?.from;

  const appliedFilters: ILocalAgreementsListQueryParams = {
    agreementId: filters.agreementId,

    endDateFrom: filters.endDate?.from
      ? convertCopenhagenStartOfDayToUtcISOString(filters.endDate.from)
      : undefined,
    endDateTo: endDateTo ? convertCopenhagenEndOfDayToUtcISOString(endDateTo) : undefined,

    status: filters.status,
    tollServiceProvider: filters.tollServiceProvider,
    agreementType: filters.agreementType,
    lpn: filters.lpn,
  };
  const params: IGetLocalAgreementsListQueryParams = {
    page,
    pageSize,
    sortOrder,
    ...appliedFilters,
  };
  const response = await osbApiAxios.get<ILocalAgreementsListResult>('/v1/localAgreements', {
    params,
  });
  return response.data;
};

export const getLocalAgreementDetails = async (id: string): Promise<LocalAgreement> => {
  const response = await osbApiAxios.get<LocalAgreement>(`/v1/localAgreements/${id}`);
  return response.data;
};

// #endregion Local Agreements

// #region Transactions

interface IGetTransactionsListQueryParams {
  page: number;
  pageSize?: number;
  sortOrder?: string;
}

export interface ITransactionsListFilter {
  transactionId: string;
  transactionDate: DateRange | undefined;
  transactionType: string;
  priceAmountStart: string;
  priceAmountEnd: string;
  tollEventTime: DateRange | undefined;

  pan: string;
  recipientName: string;
  passageId: string;
  passageType: number;
  paymentClaimReference: string;
  lpnTotal: string;
  lpnTotalCountryCode: string;
  laneNumber: number;
  localAgreementId: string;
  iTicketNumber: string;
  reasonCodeName: string;
  acceptedByBo: string;
  billingDetailsAduIdentifier: string;
}

interface ITransactionsListQueryParams {
  transactionId?: string;
  transactionDateFrom?: string;
  transactionDateTo?: string;
  transactionType?: string;
  priceAmountStart?: string;
  priceAmountEnd?: string;
  tollEventTimeFrom?: string;
  tollEventTimeTo?: string;

  pan?: string;
  recipientName?: string;
  passageId?: string;
  passageType?: number;
  paymentClaimReference?: string;
  lpnTotal?: string;
  lpnTotalCountryCode?: string;
  tollEventTime?: string;
  laneNumber?: number;
  localAgreementId?: string;
  iTicketNumber?: string;
  reasonCodeName?: string;
  acceptedByBo?: string;
  billingDetailsAduIdentifier?: string;
}

export const getTransactionsList = async (
  page: number,
  pageSize: number,
  filters: Partial<ITransactionsListFilter>,
  sortOrder?: string
): Promise<ITransactionsListResult> => {
  const transactionDateTo = filters.transactionDate?.to
    ? filters.transactionDate?.to
    : filters.transactionDate?.from;
  const tollEventTimeTo = filters.tollEventTime?.to
    ? filters.tollEventTime?.to
    : filters.tollEventTime?.from;

  const appliedFilters: ITransactionsListQueryParams = {
    transactionId: filters.transactionId,
    transactionType: filters.transactionType,
    priceAmountStart: filters.priceAmountStart,
    priceAmountEnd: filters.priceAmountEnd,
    pan: filters.pan,
    recipientName: filters.recipientName,
    passageId: filters.passageId,
    passageType: filters.passageType,
    paymentClaimReference: filters.paymentClaimReference,
    lpnTotal: filters.lpnTotal,
    lpnTotalCountryCode: filters.lpnTotalCountryCode,
    tollEventTimeFrom: filters.tollEventTime?.from
      ? convertCopenhagenDateToUtcISOString(filters.tollEventTime.from)
      : undefined,
    tollEventTimeTo: tollEventTimeTo
      ? convertCopenhagenDateToUtcISOString(tollEventTimeTo)
      : undefined,
    laneNumber: filters.laneNumber,
    localAgreementId: filters.localAgreementId,
    iTicketNumber: filters.iTicketNumber,
    reasonCodeName: filters.reasonCodeName,
    acceptedByBo: filters.acceptedByBo,
    billingDetailsAduIdentifier: filters.billingDetailsAduIdentifier,
    // transactionDate has a date time picker
    transactionDateFrom: filters.transactionDate?.from
      ? convertCopenhagenDateToUtcISOString(filters.transactionDate.from)
      : undefined,
    transactionDateTo: transactionDateTo
      ? convertCopenhagenDateToUtcISOString(transactionDateTo)
      : undefined,
  };

  const params: IGetTransactionsListQueryParams = {
    page,
    pageSize,
    sortOrder,
    ...appliedFilters,
  };
  const response = await osbApiAxios.get<ITransactionsListResult>('/v1/transactions', {
    params,
  });

  // @ts-expect-error ignore getContentType type
  const contentType: string = response?.headers?.getContentType() ?? '';

  if (!contentType.includes('application/json')) {
    throw new AxiosError(
      'Response content type is not "application/json"',
      'contentType',
      response.config,
      response.request,
      response
    );
  }
  return response.data;
};

export const getTransactionDetails = async (transactionId: string): Promise<ITransaction> => {
  const response = await osbApiAxios.get<ITransaction>(`/v1/transactions/${transactionId}`);
  return response.data;
};

export const getTransactionDetailsResend = async (transactionId: string) => {
  return osbApiAxios.get<string>(`/v1/transactions/${transactionId}/resend`);
};

// #endregion Transactions
